// Collision detection between node shapes for edge visibility

import { getShape } from '@/modules/Node';
import type { CollisionCircle, CollisionRect } from '@/modules/Node';

interface CollidesEdge {
  fromNodeShape: string;
  toNodeShape: string;
  startPosX: number;
  startPosY: number;
  endPosX: number;
  endPosY: number;
  fromNodeWidth: number;
  fromNodeHeight: number;
  fromNodeBorderWidth: number;
  fromNodeBorderWidthValue: number;
  toNodeWidth: number;
  toNodeHeight: number;
  toNodeBorderWidth: number;
  toNodeBorderWidthValue: number;
}

interface CircleNode {
  x: number;
  y: number;
  radius: number;
}

interface Rectangle1 {
  r1x: number;
  r1y: number;
  r1w: number;
  r1h: number;
}

interface Rectangle2 {
  r2x: number;
  r2y: number;
  r2w: number;
  r2h: number;
}

interface Circle {
  cx: number;
  cy: number;
  radius: number;
}

interface Rectangle {
  rx: number;
  ry: number;
  rw: number;
  rh: number;
}

function isCirclesCollides(nodeFrom: CircleNode, nodeTo: CircleNode): boolean {
  const dx = nodeTo.x - nodeFrom.x;
  const dy = nodeTo.y - nodeFrom.y;
  const rSum = nodeFrom.radius + nodeTo.radius;
  return dx * dx + dy * dy <= rSum * rSum;
}

function testRectanglesHit(rectangle1: Rectangle1, rectangle2: Rectangle2): boolean {
  const { r1x, r1y, r1w, r1h } = rectangle1;
  const { r2x, r2y, r2w, r2h } = rectangle2;

  return (
    r1x + r1w >= r2x &&
    r1x <= r2x + r2w &&
    r1y + r1h >= r2y &&
    r1y <= r2y + r2h
  );
}

function isRectanglesCollides(rectangle1: Rectangle1, rectangle2: Rectangle2): boolean {
  return testRectanglesHit(rectangle1, rectangle2);
}

function testRectangleCircleHit(circle: Circle, rectangle: Rectangle): boolean {
  const { cx, cy, radius } = circle;
  const { rx, ry, rw, rh } = rectangle;

  let testX = cx;
  let testY = cy;

  if (cx < rx) testX = rx;
  else if (cx > rx + rw) testX = rx + rw;

  if (cy < ry) testY = ry;
  else if (cy > ry + rh) testY = ry + rh;

  const distX = cx - testX;
  const distY = cy - testY;
  const distance = Math.sqrt(distX * distX + distY * distY);

  return distance <= radius;
}

function isCircleRectangleCollides(circle: Circle, rectangle: Rectangle): boolean {
  return testRectangleCircleHit(circle, rectangle);
}

export function calculateNodesCollidingForEdge(
  edge: CollidesEdge,
  nodeFrom: CircleNode,
  nodeTo: CircleNode
): boolean {
  const fromShapeDef = getShape(edge.fromNodeShape);
  const toShapeDef = getShape(edge.toNodeShape);

  const fromCollision = fromShapeDef.getCollisionData(edge, 'from');
  const toCollision = toShapeDef.getCollisionData(edge, 'to');

  // Circle-Circle
  if (fromCollision.type === 'circle' && toCollision.type === 'circle') {
    return isCirclesCollides(nodeFrom, nodeTo);
  }

  // Rect-Rect (also handles triangle AABB)
  if (fromCollision.type === 'rect' && toCollision.type === 'rect') {
    const rectangle1: Rectangle1 = {
      r1x: fromCollision.data.x,
      r1y: fromCollision.data.y,
      r1w: fromCollision.data.width,
      r1h: fromCollision.data.height,
    };
    const rectangle2: Rectangle2 = {
      r2x: toCollision.data.x,
      r2y: toCollision.data.y,
      r2w: toCollision.data.width,
      r2h: toCollision.data.height,
    };
    return isRectanglesCollides(rectangle1, rectangle2);
  }

  // Circle-Rect (or Rect-Circle)
  const circleData: CollisionCircle =
    fromCollision.type === 'circle' ? fromCollision.data : (toCollision as { type: 'circle'; data: CollisionCircle }).data;
  const rectData: CollisionRect =
    fromCollision.type === 'rect' ? fromCollision.data : (toCollision as { type: 'rect'; data: CollisionRect }).data;

  const circle: Circle = {
    cx: circleData.cx,
    cy: circleData.cy,
    radius: circleData.radius,
  };
  const rectangle: Rectangle = {
    rx: rectData.x,
    ry: rectData.y,
    rw: rectData.width,
    rh: rectData.height,
  };

  return isCircleRectangleCollides(circle, rectangle);
}
